// Copyright 2025, Imprivata, Inc.  All rights reserved.

import { MfaEnrollmentBanner } from '@common/components/MfaEnrollmentBanner';
import { Alert, Button } from '@imprivata-cloud/design-system';
import { createHrefWithCurrentSearch } from '@utils/navigationSearch';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { SessionStorageKeys } from '@/common/utils/sessionStorage';
import { RootRoutes } from '@/index';
import { SelfServicePath } from '@/self-service/SelfServiceRoutes';
import { getLandingPage } from '../../../api/portalServices';
import type { GetLandingPageResponse, PurchasedProduct } from '../../../api/types';
import DefaultLogo from '../../../assets/default_logo.svg';
import { useSession } from '../../../common/authorization/context/Session';
import {
  getDaysUntilExpiration,
  PASSWORD_EXPIRATION_WARNING_DAYS,
} from '../../../common/utils/passwordExpiration';
import { useErrorContext } from '../../../errorHandler/context/ErrorContext';
import { AppError } from '../../../errorHandler/errors';
import { productTags, ROLE_TYPE_ADMIN } from '../../constants';
import { useMfaEnrollmentStatus } from '../../hooks/useMfaEnrollmentStatus';
import { usePasswordExpirationInfo } from '../../hooks/usePasswordExpirationInfo';
import { ProductCard } from './cards/ProductCard';
import { UnpurchasedProductCard } from './cards/UnpurchasedProductCard';
import './PortalLanding.less';

const TRANSLATION_ROOT = 'portal.landing.password-expiration-banner';

export const PortalLanding = () => {
  const { emitError } = useErrorContext();
  const [data, setData] = useState<GetLandingPageResponse>();
  const { tenantId, workflowId, userInfo } = useSession();
  const [isLoading, setIsLoading] = useState<boolean>();
  const { t } = useTranslation();
  const { data: passwordInfo } = usePasswordExpirationInfo();
  const [alertDismissed, setAlertDismissed] = useState(
    () =>
      sessionStorage.getItem(SessionStorageKeys.PASSWORD_EXPIRATION_DISMISSED_PORTAL) === 'true',
  );
  const [searchParams] = useSearchParams();
  const mfaStatus = useMfaEnrollmentStatus();
  const isMfaPastDeadline = mfaStatus?.isPastDeadline ?? false;

  const daysUntilExpiration = getDaysUntilExpiration(passwordInfo?.passwordExpiresAt);
  const showPasswordAlert =
    daysUntilExpiration !== null &&
    daysUntilExpiration > 0 &&
    daysUntilExpiration <= PASSWORD_EXPIRATION_WARNING_DAYS &&
    !alertDismissed;

  useEffect(() => {
    const getPortalInfo = async () => {
      return await getLandingPage(workflowId || '', tenantId || '');
    };
    setIsLoading(true);
    getPortalInfo()
      .then(response => {
        setData(response);
      })
      .catch(e => {
        emitError(new AppError(e));
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [workflowId, tenantId, emitError]);

  // TODO: Remove this when local testing fixes the return of "string" for logos
  const resolvedLogoUrl =
    (data?.orgInfo?.logoUrl && data?.orgInfo?.logoUrl !== 'string' && data.orgInfo.logoUrl) ||
    undefined;

  const isAdmin = userInfo?.roleType?.toLowerCase() === ROLE_TYPE_ADMIN.toLowerCase();
  const purchasedProducts = (data?.productInfo?.purchasedProducts || []).filter(
    product => product.productTag !== productTags.SSO || isAdmin,
  );
  const nonPurchasedProducts = (data?.productInfo?.nonPurchasedProducts || []).filter(
    product => product.productTag !== productTags.SSO,
  );

  const passwordAlertActions = (
    <Button
      variant="default"
      size="sm"
      className="password-alert-link"
      onClick={() => {
        window.open(
          createHrefWithCurrentSearch(
            `${RootRoutes.SELF_SERVICE}${SelfServicePath.SELF_SERVICE_IDENTITY_PASSWORD}`,
            searchParams,
          ),
          '_blank',
          'noopener,noreferrer',
        );
      }}
    >
      {t(`${TRANSLATION_ROOT}.update-button`)}
    </Button>
  );

  return (
    <>
      <div className="password-expiration-alert">
        {showPasswordAlert && (
          <Alert
            status="warning"
            title={t(`${TRANSLATION_ROOT}.title`, { count: daysUntilExpiration })}
            message={t(`${TRANSLATION_ROOT}.message`)}
            actions={passwordAlertActions}
            onClose={() => {
              setAlertDismissed(true);
              sessionStorage.setItem(
                SessionStorageKeys.PASSWORD_EXPIRATION_DISMISSED_PORTAL,
                'true',
              );
            }}
          />
        )}
      </div>
      <MfaEnrollmentBanner />
      <section className="section title-bar content-title">
        <div className="product-container">
          <h1 className="h1">Imprivata Access Management</h1>
          {!isLoading && (
            <div>
              <img src={resolvedLogoUrl || DefaultLogo} alt="" />
            </div>
          )}
        </div>
      </section>
      {!isMfaPastDeadline && !!purchasedProducts?.length && (
        <section className="section purchased-cards">
          {purchasedProducts.map((product: PurchasedProduct) => (
            <ProductCard key={product.productTag} product={product} tenantId={tenantId || ''} />
          ))}
        </section>
      )}
      {!isMfaPastDeadline && !!nonPurchasedProducts?.length && (
        <section className="section unpurchased-cards">
          {nonPurchasedProducts.map(product => (
            <UnpurchasedProductCard key={product.productTag} product={product} />
          ))}
        </section>
      )}
    </>
  );
};
